import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Menu,
  LogIn,
  LogOut,
  Mail,
  BarChart3,
  Settings,
  Moon,
  Sun,
} from "lucide-react";

const DashboardHeader = ({
  user,
  isAuthenticated,
  logout,
  properties,
  totalCount,
  weeklyDigest,
  toggleDigest,
  currentMortgageRate,
  subscription,
}) => {
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") return document.documentElement.classList.contains("dark");
    return false;
  });

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("relixdata_theme", next ? "dark" : "light");
  };

  useEffect(() => {
    const saved = localStorage.getItem("relixdata_theme");
    if (saved === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  return (
    <header className="glass-effect sticky top-0 z-50 border-b border-border/50">
      {/* Logo + Nav */}
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-3 sm:px-6 py-2.5 sm:py-4">
        <div className="flex items-center gap-2.5 sm:gap-4 cursor-pointer flex-shrink-0" onClick={() => navigate("/")} data-testid="header-logo-area">
          <img
            src="/logo.png"
            alt="RealixData"
            className="h-8 sm:h-12 w-auto"
            data-testid="header-logo"
          />
          <span className="text-lg sm:text-2xl font-extrabold tracking-tight text-foreground">
            RealixData
          </span>
        </div>
        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <button
            onClick={() => navigate("/top-deals")}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            data-testid="nav-top-deals"
          >
            Top Deals
          </button>
          <button
            onClick={() => navigate("/create-report")}
            className="px-3 py-1.5 rounded-md text-sm text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors font-medium"
            data-testid="nav-create-report"
          >
            Create Report
          </button>
          <button
            onClick={() => navigate("/best-rental-markets")}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            data-testid="nav-rental-markets"
          >
            Rental Markets
          </button>
          <button
            onClick={() => navigate("/pricing")}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            data-testid="nav-pricing"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate("/faq")}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            data-testid="nav-faq"
          >
            FAQ
          </button>
        </nav>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden md:flex items-center gap-4">
            {subscription && !subscription.is_paid && subscription.analysis_limit > 0 && (
              <div className="text-center" data-testid="usage-counter">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  Analyses
                </p>
                <p className={`font-mono text-base font-bold ${subscription.analyses_remaining <= 2 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {subscription.analyses_remaining}/{subscription.analysis_limit}
                </p>
              </div>
            )}
            {currentMortgageRate && (
              <div className="text-center" data-testid="mortgage-rate-badge">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  30yr Rate
                </p>
                <p className="font-mono text-base font-bold text-amber-500">
                  {currentMortgageRate.rate}%
                </p>
              </div>
            )}
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Results
              </p>
              <p className="font-mono text-base font-bold">
                {properties.length}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                Total
              </p>
              <p className="font-mono text-base font-bold text-profit">
                {totalCount.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="relative" ref={settingsRef}>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="p-2 rounded-lg hover:bg-secondary/60 transition-colors"
              data-testid="settings-menu-btn"
            >
              <Menu className="w-5 h-5 text-muted-foreground" />
            </button>
            {settingsOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in"
                data-testid="settings-dropdown"
              >
                <div className="p-4 border-b border-border">
                  {isAuthenticated && user ? (
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {user.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Preview Mode
                      </p>
                      <Button
                        size="sm"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => navigate("/auth")}
                        data-testid="settings-login-btn"
                      >
                        <LogIn className="w-3.5 h-3.5 mr-1.5" />
                        Sign In / Sign Up
                      </Button>
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  {/* Mobile-only nav links */}
                  <div className="md:hidden space-y-1 pb-2 border-b border-border mb-2">
                    <button onClick={() => { setSettingsOpen(false); navigate("/top-deals"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors" data-testid="mobile-nav-top-deals">
                      Top Deals
                    </button>
                    <button onClick={() => { setSettingsOpen(false); navigate("/create-report"); }} className="flex items-center gap-2 text-sm text-emerald-500 hover:text-emerald-400 w-full py-1.5 transition-colors font-medium" data-testid="mobile-nav-create-report">
                      Create Report
                    </button>
                    <button onClick={() => { setSettingsOpen(false); navigate("/best-rental-markets"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors" data-testid="mobile-nav-rental-markets">
                      Rental Markets
                    </button>
                    <button onClick={() => { setSettingsOpen(false); navigate("/pricing"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors" data-testid="mobile-nav-pricing">
                      Pricing
                    </button>
                    <button onClick={() => { setSettingsOpen(false); navigate("/faq"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors" data-testid="mobile-nav-faq">
                      FAQ
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      {darkMode ? <Moon className="w-4 h-4 text-muted-foreground" /> : <Sun className="w-4 h-4 text-muted-foreground" />}
                      <span className="text-sm font-medium">Dark Mode</span>
                    </div>
                    <button
                      onClick={toggleDarkMode}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        darkMode ? "bg-emerald-500" : "bg-muted"
                      }`}
                      data-testid="dark-mode-toggle"
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                          darkMode ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Weekly Digest</span>
                    </div>
                    <button
                      onClick={toggleDigest}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        weeklyDigest ? "bg-emerald-500" : "bg-muted"
                      }`}
                      data-testid="digest-toggle"
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                          weeklyDigest ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {weeklyDigest
                      ? "Emails every Monday"
                      : "Opt in for weekly deals"}
                  </p>
                  {isAuthenticated && (
                    <>
                      <button
                        onClick={() => {
                          setSettingsOpen(false);
                          navigate("/settings");
                        }}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors"
                        data-testid="settings-link"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                      {user?.email === "austinreeseajr99@gmail.com" && (
                      <button
                        onClick={() => {
                          setSettingsOpen(false);
                          navigate("/admin");
                        }}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full py-1.5 transition-colors"
                        data-testid="admin-link"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Analytics Dashboard
                      </button>
                      )}
                      <button
                        onClick={() => {
                          setSettingsOpen(false);
                          logout();
                          navigate("/");
                        }}
                        className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 w-full py-1.5 pt-3 border-t border-border transition-colors"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        Log out
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
